import { Select, Tooltip, Button } from 'antd';
import React from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';

export const groupDrugsByTier = (drugs) => {
  console.log('Grouping drugs by tier:', drugs);
  const tierGroups = {};

  drugs.forEach(drug => {
    if (!drug.tier_level_value) {
      console.warn(`Drug ${drug.proprietaryname} is missing tier_level_value`);
      return;
    }

    const tier = drug.tier_level_value;
    if (!tierGroups[tier]) {
      tierGroups[tier] = [];
    }
    tierGroups[tier].push(drug);
  });

  return tierGroups;
};

export const groupDrugsByNonproprietaryName = (drugs) => {
  console.log('Grouping drugs by nonproprietary name:', drugs);
  const groups = {};
  let nullNonproprietaryNameIndex = 0; // To handle drugs with null nonproprietaryname

  drugs.forEach(drug => {
    // If nonproprietaryname is null, create a unique key for each drug
    const key = drug.nonproprietaryname === null ? `null-${nullNonproprietaryNameIndex++}` : drug.nonproprietaryname;

    if (!groups[key]) {
      groups[key] = {
        ...drug,
        proprietarynames: [drug.proprietaryname],
        details: [drug] // Collecting all the details for expandable rows
      };
    } else {
      // If nonproprietaryname is not null, group as before
      groups[key].proprietarynames.push(drug.proprietaryname);
      groups[key].details.push(drug);
    }
  });

  return Object.values(groups); // We only need the grouped drugs for rendering
};

export const sortByTier = (groups, sortOrder) => {
  console.log('Sorting by tier:', { groups, sortOrder });
  return groups.sort((a, b) => {
    let tierA = a.tier_level_value === 'Not Covered' ? 'Not Covered' : parseInt(a.tier_level_value, 10);
    let tierB = b.tier_level_value === 'Not Covered' ? 'Not Covered' : parseInt(b.tier_level_value, 10);

    let indexA = sortOrder.indexOf(tierA);
    let indexB = sortOrder.indexOf(tierB);

    if (indexA === -1) indexA = sortOrder.length;
    if (indexB === -1) indexB = sortOrder.length;

    return indexA - indexB;
  });
};

export const getCustomTierSortOrder = (hasTierSix) => {
  console.log('Determining custom tier sort order. Has tier 6:', hasTierSix);
  return hasTierSix ? [1, 2, 6, 3, 4, 5, 'Not Covered'] : [1, 2, 3, 4, 5, 6, 'Not Covered'];
};


export const getProprietaryNameExpandedRows = (nonproprietaryname, groupedResults) => {
  console.log('Getting proprietary name expanded rows for:', nonproprietaryname);
  const group = groupedResults.find(group => group.nonproprietaryname === nonproprietaryname);
  return group ? group.details : [];
};


export const proprietaryNameDetailColumns = () => [
  {
    dataIndex: 'proprietaryname',
    title: 'Description',
  },
  {
    dataIndex: 'tier_level_value',
    title: 'Tier Level Value',
    render: (tierLevelValue) => {
      return (
        <span>
          {tierLevelValue}
        </span>
      );
    },
  },
];

const formatCost = (  
  costType,
  amount,
  retailCost = null,
  deductible = 0,
  coverageLevel1 = null
) => {
  // Default object in case we return early
  const defaultResult = {
    costDisplay: 'N/A',
    explanation: '',
    isDeductibleStage: false
  };

  // If amount is missing => show 'N/A' unless it's costType=3 with no retail cost
  if (amount === undefined || amount === null || amount === 'N/A') {
    // If costType=3 but we have no valid retailCost => treat as 100% patient responsibility
    if (costType === 3) {
      return {
        costDisplay: '100%',
        explanation: 'No retail cost data found. Patient pays 100% at deductible stage until meet dedutible.',
        isDeductibleStage: true
      };
    }
    return defaultResult;
  }

  // ----------------------------------
  // costType=3 => Deductible Logic
  // ----------------------------------
  if (costType === 3) {
    // If no valid retail cost => treat as 100%
    if (typeof retailCost !== 'number') {
      return {
        costDisplay: '100%',
        explanation: 'No retail cost data found. Patient pays 100% at deductible stage until meet dedutible.',
        isDeductibleStage: true
      };
    }

    // This signals we are in the deductible stage
    let isDeductibleStage = true;

    // 1) If retail cost <= deductible => pay 100% of retail cost
    if (retailCost <= deductible) {
      return {
        costDisplay: `$${retailCost.toFixed(2)}`,
        explanation: `Retail cost ($${retailCost.toFixed(
          2
        )}) <= deductible ($${deductible.toFixed(2)}). Patient pays full cost.`,
        isDeductibleStage
      };
    }

    // 2) If retail cost > deductible => difference = retail - deductible
    const costDifference = retailCost - deductible;
    if (!coverageLevel1) {
      // Fallback if coverage-level-1 missing
      return {
        costDisplay: `$${retailCost.toFixed(2)}`,
        explanation: `No coverage-level-1 info; default to full retail cost.`,
        isDeductibleStage
      };
    }

    let finalCost = 0;
    let explanation = '';
    const cov1Type = parseInt(coverageLevel1.cost_type_pref, 10);
    const cov1Amount = parseFloat(coverageLevel1.cost_amt_pref);

    // coverage-level-1 is a percentage
    if (cov1Type === 2) {
      finalCost = deductible + costDifference * cov1Amount;
      explanation = `Retail cost = $${retailCost.toFixed(
        2
      )} > deductible = $${deductible.toFixed(
        2
      )}. Pay $${deductible.toFixed(
        2
      )} + ${(cov1Amount * 100).toFixed(
        0
      )}% of $${costDifference.toFixed(2)} = $${finalCost.toFixed(2)}.`;
    }
    // coverage-level-1 is a fixed copay
    else if (cov1Type === 1) {
      finalCost = deductible + cov1Amount;
      explanation = `Retail cost = $${retailCost.toFixed(
        2
      )} > deductible = $${deductible.toFixed(
        2
      )}. Pay $${deductible.toFixed(2)} + $${cov1Amount.toFixed(
        2
      )} = $${finalCost.toFixed(2)}.`;
    } else {
      // Fallback if not recognized
      finalCost = retailCost;
      explanation = `Could not determine coverage-level-1 type; default full cost = $${retailCost.toFixed(2)}.`;
    }

    return {
      costDisplay: `$${finalCost.toFixed(2)}`,
      explanation,
      isDeductibleStage
    };
  }

  // ----------------------------------
  // Other cost types
  // ----------------------------------
  if (costType === 0) {
    return {
      costDisplay: `$${parseFloat(amount).toFixed(2)}`,
      explanation: '',
      isDeductibleStage: false
    };
  }

  if (costType === 2) {
    const percentage = parseFloat(amount);
    let display = `${(percentage * 100).toFixed(0)}%`;
    let explanation = '';
    if (retailCost) {
      const costVal = (retailCost * percentage).toFixed(2);
      display += ` ($${costVal})`;
      explanation = `Pay ${(percentage * 100).toFixed(0)}% of $${retailCost.toFixed(2)}.`;
    }
    return {
      costDisplay: display,
      explanation,
      isDeductibleStage: false
    };
  }

  if (costType === 1) {
    return {
      costDisplay: `$${parseFloat(amount).toFixed(2)}`,
      explanation: '',
      isDeductibleStage: false
    };
  }

  // Fallback if unknown
  return {
    costDisplay: amount,
    explanation: '',
    isDeductibleStage: false
  };
};


export const renderRequirements = (drug, selectedInsurancePlan, handlePopupOpen, sendEmailNotification) => {
  console.log('Rendering requirements for drug:', drug);
  const requirements = Array.isArray(drug.requirements) ? drug.requirements : [drug.requirements];
  if (drug.STEP_THERAPY_YN === 'Y') {
    requirements.push('Step Therapy required');
  }

  const quantityDetails = requirements.find(req => req.startsWith('Quantity Limit Amount')) || '';
  const formattedQuantityDetails = quantityDetails ? quantityDetails.replace('Quantity Limit Amount: ', '').replace(', Quantity Limit Days: ', ' per ') : '';

  return (
    <div>
      {requirements.map((requirement, index) => {
        if (requirement === 'Prior Authorization required') {
          // grab the detail object your backend returned
          const pa = drug.prior_authorization_details || {};

          // build the popup content as JSX
          const popupContent = (
            <div style={{
              maxHeight: 10000,
              overflowY: 'auto',
              padding: '1rem',
              lineHeight: 1.6,
              background: '#fff',
              }}>
              {pa.error ? (
                // 1) Show error if present
                <p style={{ color: 'Grey' }}>
                  <strong>We are so sorry:</strong> {pa.error}
                </p>
              ) : (
                // 2) Otherwise show all PA fields
              <>
              
              {pa.drug_name && (
                <p>
                  <strong>Drug Name: </strong> {pa.drug_name}
                </p>
              )}
              {pa.products_affected && (
                <p>
                  <strong>Products Affected: </strong> {pa.products_affected}
                </p>
              )}
              {pa.indications && (
                <p>
                  <strong>Indications:</strong> {pa.indications}
                </p>
              )}
              {pa.off_label_uses && (
                <p>
                  <strong>Off-label Uses:</strong> {pa.off_label_uses}
                </p>
              )}
              {pa.exclusion_criteria && (
                <p>
                  <strong>Exclusion Criteria:</strong> {pa.exclusion_criteria}
                </p>
              )}
              {pa.required_medical_information && (
                <p>
                  <strong>Required Medical Information:</strong>{' '}
                  {pa.required_medical_information}
                </p>
              )}
              {pa.age_restrictions && (
                <p>
                  <strong>Age Restrictions:</strong> {pa.age_restrictions}
                </p>
              )}
              {pa.prescriber_restrictions && (
                <p>
                  <strong>Prescriber Restrictions:</strong>{' '}
                  {pa.prescriber_restrictions}
                </p>
              )}
              {pa.coverage_duration && (
                <p>
                  <strong>Coverage Duration:</strong> {pa.coverage_duration}
                </p>
              )}
              {pa.other_criteria && (
                <p>
                  <strong>Other Criteria:</strong> {pa.other_criteria}
                </p>
              )}
              {pa.part_b_prerequisite && (
                <p>
                  <strong>Part B Prerequisite:</strong> {pa.part_b_prerequisite}
                </p>
              )}
            </>
          )}
          {pa.start_pa_link && (
            <p>
              <a
                href={pa.start_pa_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Start Prior Authorization
              </a>
            </p>
          )}

          {/* 3) Show links if any */}
          {(pa.pa_link || pa.st_link || pa.document_link) && (
                <div style={{ marginTop: '1em' }}>
                  <strong>Documents:</strong>
                  <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                    {pa.pa_link && (
                      <li>
                        <a href={pa.pa_link} target="_blank" rel="noopener noreferrer">
                          Prior Authorization PDF
                        </a>
                      </li>
                    )}
                    {pa.st_link && (
                      <li>
                        <a href={pa.st_link} target="_blank" rel="noopener noreferrer">
                          Step Therapy PDF
                        </a>
                      </li>
                    )}
                    {pa.document_link && (
                      <li>
                        <a href={pa.document_link} target="_blank" rel="noopener noreferrer">
                          Plan Documents
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          );

          return (
            <div key={index}>
              Prior Authorization required{' '}
              <span
                onClick={() => {
                  // console.log("🔍 [renderRequirements] drug:", drug.nonproprietaryname);
                  // console.log("🔍 prior_authorization_details:", pa);
                  // console.log("🔍 popupContent (JSX):", popupContent);
                  // console.log("🔍 pa.pa_link:", pa.pa_link);
                  // console.log("🔍 pa.st_link:", pa.st_link);
                  // console.log("🔍 pa.document_link:", pa.document_link);
                  handlePopupOpen(drug.nonproprietaryname, popupContent, drug.prior_authorization_details?.error);
                }}
                style={{
                  cursor: 'pointer',
                  marginLeft: 2,
                  color: '#007bff',   
                }}
              >
                (View Details)
              </span>
            </div>
          );
        
        } else if (requirement === 'Quantity Limits apply') {
          return (
            <div key={index}>
              QL:{formattedQuantityDetails} days
            </div>
          );
        } else if (requirement.startsWith('Quantity Limit Amount')) {
          return null;
        } else {
          return <div key={index}>{requirement}</div>;
        }
      })}
    </div>
  );
}



export const resultTableColumns = (selectedDaysSupply, selectedPharmacyType, handlePopupOpen, selectedInsurancePlan, selectedPlanDetails, drugCosts, showMonthlyBreakdownModal) => {
  console.log('Generating result table columns with:', { selectedDaysSupply, selectedPharmacyType, selectedInsurancePlan, selectedPlanDetails });
  const tierLevelDescriptions = {
    1: 'Preferred Generic Drug',
    2: 'Generic Drug',
    3: 'Preferred Brand Drug',
    4: 'Non-Preferred Drug',
    5: 'Specialty Tier Drug',
    6: 'Select Care Drug',
  };

  const tierTooltipContent = (
    <div>
      <p>Tier 1: {tierLevelDescriptions[1]}</p>
      <p>Tier 2: {tierLevelDescriptions[2]}</p>
      <p>Tier 3: {tierLevelDescriptions[3]}</p>
      <p>Tier 4: {tierLevelDescriptions[4]}</p>
      <p>Tier 5: {tierLevelDescriptions[5]}</p>
      <p>Tier 6: {tierLevelDescriptions[6]}</p>
    </div>
  );

  const patientOutOfPocketCostRender = (record, selectedDaysSupply, selectedPharmacyType, deductible, drugCosts, showMonthlyBreakdownModal) => {
    // console.log('drugCosts:', drugCosts);

  if (!Array.isArray(record.out_of_pocket_cost)) {
    return 'No cost data available';
  }

  const daysSupplyValue = selectedDaysSupply === '30days' ? 1 : 2;
  const isInsulin = record.out_of_pocket_cost.some(cost => cost.coverage_level === undefined);
  // const costHeaderSuffix = selectedPharmacyType === 'retail' ? 'Pharmacy' : 'Mail Pharmacy';
  // const costHeader = `Preferred ${costHeaderSuffix} Cost / ${selectedDaysSupply.replace('days', ' Days')}`;

  const mailOrderNotOffered = record.out_of_pocket_cost.some(cost =>
    cost.days_supply === daysSupplyValue &&
    cost.cost_amt_mail_pref === 'not offered' &&
    cost.cost_amt_mail_nonpref === 'not offered'
  );

  // If the mail order benefit is not offered, display the message and do not render the rest of the table
  if (mailOrderNotOffered && selectedPharmacyType === 'mail') {
    return <p>The plan doesn’t offer a mail order benefit.</p>;
  }

  if (deductible > 0 && !record.out_of_pocket_cost.some(cost => cost.coverage_level === 0 && cost.days_supply === daysSupplyValue)) {
    const deductibleStageEntry = {
      coverage_level: 0,
      days_supply: daysSupplyValue,
      cost_type_pref: 3, // This could be a special indicator for deductible stage.
      cost_type_nonpref: 3, // Assuming the same applies for non-preferred.
      cost_type_mail_pref: 3, // Assuming the same applies for mail preferred.
      cost_type_mail_nonpref: 3, // Assuming the same applies for mail non-preferred.
      cost_amt_pref: '1.00', // 100% cost to patient during the deductible stage.
      cost_amt_nonpref: '1.00', // Assuming 100% for non-preferred as well.
      cost_amt_mail_pref: '1.00', // Assuming 100% for mail order preferred.
      cost_amt_mail_nonpref: '1.00' // Assuming 100% for mail order non-preferred.
    };
    record.out_of_pocket_cost.unshift(deductibleStageEntry);
  }

  // If the drug is insulin, process using insulin-specific logic
  if (isInsulin) {
    return processAndDisplayInsulinCostNew(record, selectedDaysSupply, selectedPharmacyType, showMonthlyBreakdownModal);
  }

  // Map retail costs using nonproprietaryname

  const retailCostMap = {};

  if (Array.isArray(drugCosts)) {
    console.log("Processing drugCosts for record:", record.nonproprietaryname);

    drugCosts.forEach(cost => {
      const drugNameKey = cost.drug_name.toLowerCase();
      console.log("Mapping drug_name to retail_cost:", {
        drug_name: cost.drug_name,
        retail_cost: cost.retail_cost,
        pharmacy_name: cost.pharmacy_name,
      });

      // Initialize drugNameKey if not already in the map
      if (!retailCostMap[drugNameKey]) {
        retailCostMap[drugNameKey] = {
          retail: null, // For retail pharmacy costs
          mail: null, // For mail order pharmacy costs
        };
      }

      // Assign costs based on pharmacy type
      if (cost.pharmacy_name.toLowerCase().includes("mail")) {
        retailCostMap[drugNameKey].mail = parseFloat(cost.retail_cost || 0);
      } else {
        retailCostMap[drugNameKey].retail = parseFloat(cost.retail_cost || 0);
      }
    });

    console.log("Completed retailCostMap with pharmacy classification:", retailCostMap);
  } else {
    console.log("No drug_costs array found in record:", drugCosts);
  }

  // Safely handle null or undefined nonproprietaryname
  let nonproprietaryKey;
  if (record.nonproprietaryname) {
          // Remove "(generic)" if present (case-insensitive), then lowercase and trim.
          nonproprietaryKey = record.nonproprietaryname.replace(/\(generic\)/i, "").toLowerCase().trim();
  } else if (record.proprietaryname) {
    // Extract main drug name from proprietaryname (use the first word as a heuristic)
    nonproprietaryKey = record.proprietaryname.split(" ")[0].toLowerCase();
  } else {
    nonproprietaryKey = ""; // Default to empty string if neither is available
  }

  // console.log("Determined key for matching drug costs:", nonproprietaryKey);
  
  const matchingCosts = Object.entries(retailCostMap).find(([drugNameKey]) => 
    drugNameKey.includes(nonproprietaryKey) // Partial string match
  );

  // Default to empty object if no match is found
  const retailCosts = matchingCosts ? matchingCosts[1] : { retail: 0, mail: 0 };

  console.log("Retail costs for nonproprietaryname with classification:", {
    nonproprietaryKey,
    retailCosts,
  });

  let selectedPharmacyCost = 0;

  // Adjust retail cost based on days supply and pharmacy type
  if (selectedPharmacyType === "mail") {
    // Mail order cost is for 90 days; adjust if 30 days is selected
    selectedPharmacyCost = selectedDaysSupply === '30days' 
      ? (retailCosts.mail !== null && retailCosts.mail !== 0 ? retailCosts.mail / 3 : null) 
      : (retailCosts.mail !== null && retailCosts.mail !== 0 ? retailCosts.mail : null);
  } else {
    // Retail cost is for 30 days; adjust if 90 days is selected
    selectedPharmacyCost = selectedDaysSupply === '30days' 
      ? (retailCosts.retail !== null && retailCosts.retail !== 0 ? retailCosts.retail : null) 
      : (retailCosts.retail !== null && retailCosts.retail !== 0 ? retailCosts.retail * 3 : null);
  }

  // Handle case where cost data is missing
  let selectedPharmacyCostDisplay; // Separate display value
  if (selectedPharmacyCost === null || selectedPharmacyCost === undefined) {
    selectedPharmacyCostDisplay = "N/A";
  } else {
    selectedPharmacyCostDisplay = `$${selectedPharmacyCost.toFixed(2)}`;
  }

  console.log("Adjusted pharmacy cost based on supply and type:", {
    selectedPharmacyType,
    selectedDaysSupply,
    selectedPharmacyCost,
    selectedPharmacyCostDisplay,
  });

  const coverageLevel1Entry = record.out_of_pocket_cost.find(
    (c) => c.coverage_level === 1 && c.days_supply === daysSupplyValue
  );

  const sortedAndFilteredCosts = record.out_of_pocket_cost
    .filter(cost => cost.days_supply === daysSupplyValue && cost.coverage_level !== 2)
    .sort((a, b) => a.coverage_level - b.coverage_level);

  if (sortedAndFilteredCosts.length === 0) {
      console.log("No valid out_of_pocket_cost data for record:", record);
      return 'No cost data available for the selected options';
  }
  
    const tableRows = sortedAndFilteredCosts.map((cost, index) => {
      const coverageWord = getCoverageLevelWord(cost.coverage_level);
      
      // Only display retail cost for the first row
      const retailCostDisplay = index === 0 ? selectedPharmacyCostDisplay : '';

      let pharmacyKey;
      if (cost.coverage_level === 0) {
        pharmacyKey = 'cost_amt_pref'; // Deductible stage always uses preferred cost
      } else {
        pharmacyKey = selectedPharmacyType === 'retail' ? 'cost_amt_pref' : 'cost_amt_mail_pref';
      }
      const mailOrderNotOffered = (pharmacyKey.includes('mail') && 
                                      cost.cost_amt_mail_pref === 'not offered' &&
                                      cost.cost_amt_mail_nonpref === 'not offered');

      
      // In patientOutOfPocketCostRender, inside tableRows.map(...):
      let costCell;
    // 1) If coverage_level=3 => Catastrophic => $0.00 always
    if (cost.coverage_level === 3) {
      costCell = (
        <span>
          $0.00{" "}
          {/* <Button 
            type="link" 
            onClick={() => showMonthlyBreakdownModal(record)}
            style={{ paddingLeft: 0 }}
          >
            View Monthly Breakdown
          </Button> */}
        </span>
      );
    }
    // 2) If mail order not offered
    else if (mailOrderNotOffered) {
      costCell = 'The plan doesn’t offer a mail order benefit';
    }
    // 3) Otherwise, use `formatCost`
    else {
      const { costDisplay, explanation, isDeductibleStage } = formatCost(
        cost.cost_type_pref,
        cost[pharmacyKey],
        typeof selectedPharmacyCost === 'number' ? selectedPharmacyCost : null,
        deductible,
        coverageLevel1Entry
      );

      // If costType=3 => show question icon + tooltip
      if (isDeductibleStage) {
        costCell = (
          <Tooltip placement="bottom" title={explanation}>
            <span style={{ cursor: 'pointer' }}>
              {costDisplay} <QuestionCircleOutlined style={{ marginLeft: 4 }} />
            </span>
          </Tooltip>
        );
      } else {
        costCell = costDisplay;
      }
    }
      // if (index === 0) {
      // // console.log('First cost entry:', cost);
      // // console.log('First row display:', `${coverageWord}: ${costFormatted}`);
      // }

      console.log("Table row data:", {
        coverageWord,
        retailCostDisplay,
        costCell,
      });
  
      return (
      <tr key={index}>
      <td>{coverageWord}</td>
      <td>{costCell}</td>
      <td>{retailCostDisplay}</td> {/* Display retail cost only for the first row */}
      </tr>
      );
      });

      // --- [NEW] If coverage_level=3 is present, add a new row for "View Monthly Breakdown"
      const coverageLevel3Exists = sortedAndFilteredCosts.some(
        cost => cost.coverage_level === 3
      );

      if (coverageLevel3Exists) {
        tableRows.push(
          <tr key="view-monthly-breakdown-row">
            <td colSpan={3} style={{ textAlign: "left" }}>
            <Button
              type="link"
              onClick={() => showMonthlyBreakdownModal({
                // merge everything here:
                ...record,
                planDetails: selectedPlanDetails,
                drug_costs: drugCosts,
                results: sortedAndFilteredCosts
              })}
            >
                View Monthly Breakdown
              </Button>
            </td>
          </tr>
        );
      }

      return (
      <table>
      <thead>
      <tr>
      <th style={{ textAlign: 'left'}}>Coverage Level</th>
      <th>{/*costHeader */}</th> {/* Updated header */}
      <th>Retail Cost</th>
      </tr>
      </thead>
      <tbody>{tableRows}</tbody>
      </table>
      );
};


  return [
    {
      dataIndex: 'proprietaryname',
      title: 'Drug Name',
      render(value, record) {
        const displayValue = record.nonproprietaryname || value; // Use nonproprietaryname if available, otherwise proprietaryname
        return (
          <div>
            {record.nonproprietaryname
              ? <strong>{displayValue}</strong> // Bold if nonproprietaryname is available
              : displayValue // Normal text if nonproprietaryname is missing
            }
          </div>
        );
      },
    },
    {
      dataIndex: 'tier_level_value',
      title: (
        <Tooltip placement="bottom" title={tierTooltipContent}>
          Tier Level Value <QuestionCircleOutlined />
        </Tooltip>
      ),
      render: (tierLevelValue, record) => {
        // Return only 'Not Covered' if the tier level is not available, without 'Unknown Tier'
        if (tierLevelValue === 'Not Covered') return 'Not Covered';
        
        const description = tierLevelDescriptions[tierLevelValue];
        const deductible = selectedPlanDetails?.deductible || 0; // Safely access deductible or default to 0

        // Only show the message if there is a deductible
        const deductibleMessage = deductible > 0 && record.out_of_pocket_cost.some(cost => cost.ded_applies_yn === 'N') ? (
          <div style={{ fontSize: 'smaller', color: '#888', marginTop: '4px' }}>
            (Deductible does not apply to this tier)
          </div>
        ) : null;
        return (
          <div>
            {description ? `${tierLevelValue} (${description})` : tierLevelValue}
            {deductibleMessage}
          </div>
        );
      },
    },
    {
      dataIndex: 'out_of_pocket_cost',
      title: (
        <div>
          Patient Out-of-pocket ($)
          <div style={{ fontSize: 'smaller', color: '#888', lineHeight: 1.5 }}>
            <div>(All costs are estimates for reference)</div>
            <div>(% is the percentage of the drug retail price)</div>
          </div>
        </div>
      ),
      render: (_, record) => patientOutOfPocketCostRender(
        record, 
        selectedDaysSupply, 
        selectedPharmacyType, 
        selectedPlanDetails ? selectedPlanDetails.deductible : 0, // Safely access deductible or default to 0
        drugCosts,
        showMonthlyBreakdownModal
      ),
    },
    {
      dataIndex: 'active_ingred_unit',
      title: 'Coverage Requirements',
      render: (_, drug) => {
        return drug.covered ? renderRequirements(drug, selectedInsurancePlan, handlePopupOpen) : '';
      },
    },
  ];
};

export const getCoverageLevelWord = (level) => {
  const coverageLevels = {
    0: '0. Deductible',
    1: '1. Initial Coverage',
    // 2: '2. Coverage Gap',
    3: '2. Catastrophic',
  };
  return coverageLevels[level] || 'Unknown';
};

export const SupplyDurationSelector = ({ value, onChange }) => {
  console.log('Rendering SupplyDurationSelector with value:', value);
  return (
    <Select value={value} style={{ width: 120 }} onChange={onChange}>
      <Select.Option value="30days">30 Days</Select.Option>
      <Select.Option value="90days">90 Days</Select.Option>
    </Select>
  );
};

export const PharmacyTypeSelector = ({ value, onChange }) => {
  console.log('Rendering PharmacyTypeSelector with value:', value);
  return (
    <Select value={value} style={{ width: 160 }} onChange={onChange}>
      <Select.Option value="retail">Retail Pharmacy</Select.Option>
      <Select.Option value="mail">Mail Pharmacy</Select.Option>
    </Select>
  );
};

const processAndDisplayInsulinCostNew = (record, selectedDaysSupply, pharmacyType, showMonthlyBreakdownModal) => {
    // Extract the insulin cost data from the record.
    const insulinCostData = record.out_of_pocket_cost;

    if (!Array.isArray(insulinCostData)) {
      return 'No insulin cost data available'; // Placeholder for no data
    }
  
    const daysSupplyValue = selectedDaysSupply === '30days' ? 1 : 2;
    const coverageLevels = {
      0: '0. Deductibles',
      1: '1. Initial Coverage',
      3: '2. Catastrophic',
    };
  
    let coverageInfo = [
      { level: '0', preferredCost: 'N/A', nonPreferredCost: 'N/A' },
      { level: '1', preferredCost: 'N/A', nonPreferredCost: 'N/A' }, // Assume 'N/A' by default
      // { level: '2', preferredCost: 'N/A', nonPreferredCost: 'N/A' }, // Assume 'N/A' by default
      { level: '3', preferredCost: 0, nonPreferredCost: 0 }, // Catastrophic coverage with $0 cost
    ];
  
    insulinCostData.forEach(cost => {
      if (cost.days_supply === daysSupplyValue) {
        let preferredCost, nonPreferredCost;
        if (pharmacyType === 'retail') {
          preferredCost = cost.copay_amt_pref_insln !== null ? cost.copay_amt_pref_insln : cost.copay_amt_nonpref_insln;
          nonPreferredCost = cost.copay_amt_nonpref_insln;
        } else { // Handling for mail pharmacy
          preferredCost = cost.copay_amt_mail_pref_insln !== null ? cost.copay_amt_mail_pref_insln : cost.copay_amt_mail_nonpref_insln;
          nonPreferredCost = cost.copay_amt_mail_nonpref_insln;
        }
  
        preferredCost = (preferredCost !== null && preferredCost !== undefined) ? preferredCost.toString() : 'N/A';
        nonPreferredCost = (nonPreferredCost !== null && nonPreferredCost !== undefined) ? nonPreferredCost.toString() : 'N/A';
        
        coverageInfo[0].preferredCost = preferredCost;
        coverageInfo[0].nonPreferredCost = nonPreferredCost;
        coverageInfo[1].preferredCost = preferredCost;
        coverageInfo[1].nonPreferredCost = nonPreferredCost;
        // coverageInfo[2].preferredCost = preferredCost; // Assuming coverage gap has the same cost
        // coverageInfo[2].nonPreferredCost = nonPreferredCost;
      }
    });
  
    // Adjust level 3 costs based on levels 1 and 2
    if (coverageInfo[1].preferredCost === 'N/A' && coverageInfo[2].preferredCost === 'N/A') {
      coverageInfo[3].preferredCost = 'N/A';
    }
    if (coverageInfo[1].nonPreferredCost === 'N/A' && coverageInfo[2].nonPreferredCost === 'N/A') {
      coverageInfo[3].nonPreferredCost = 'N/A';
    }
  
    // Map the coverageInfo to table rows, excluding non-preferred pharmacy costs
    const tableRows = coverageInfo.map((info, index) => {
      const coverageWord = coverageLevels[info.level];
      const formattedPreferredCost = info.preferredCost !== 'N/A' ? `$${parseFloat(info.preferredCost).toFixed(2)}` : 'N/A';
      
      return (
        <tr key={index}>
          <td>{coverageWord}</td>
          <td>{formattedPreferredCost}</td>
        </tr>
      );
    });
  
    // If catastrophic (coverage level 3) exists in our data, append a separate row
    // for the "View Monthly Breakdown" link.
    const catastrophicExists = coverageInfo.some(info => info.level === '3');
    if (catastrophicExists) {
      tableRows.push(
        <tr key="view-monthly-breakdown-insulin">
          {/* Leave the first column blank and merge the second column */}
          <td colSpan={3} style={{ textAlign: "left" }}>
            <Button
              type="link"
              onClick={() => showMonthlyBreakdownModal(record)}
            >
              View Monthly Breakdown
            </Button>
          </td>
        </tr>
      );
    }
  
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left'}}>Coverage Level</th>
            <th></th>
          </tr>
        </thead>
        <tbody>{tableRows}</tbody>
      </table>
    );
  };